import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import {
  buildDesktopAuthBridgeUrl,
  signInWithGoogle,
  signOut as signOutFromSupabase,
  supabase,
} from "../lib/supabase";
import { parseAuthParamsFromUrl } from "../lib/auth-callback";
import {
  clearWebAuthCallbackHistory,
  restoreSessionFromAuthParams,
  restoreWebAuthSession,
} from "../lib/web-auth-session";
import { useDesktopAuthCallbacks } from "./desktop-auth-callbacks";
import { getRuntimeApiBase } from "../lib/runtime-api-base";
import { isWaitlistOnly } from "../lib/waitlist-access-policy";

interface UseAuthSessionResult {
  isLoading: boolean;
  session: Session | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  user: User | null;
}

export function useAuthSession(): UseAuthSessionResult {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const applySession = useCallback((nextSession: Session | null) => {
    setSession(nextSession);
    setUser(nextSession?.user ?? null);
  }, []);

  const applyApprovedSession = useCallback(
    async (nextSession: Session | null) => {
      if (nextSession && !(await isApprovedSession(nextSession))) {
        await supabase?.auth.signOut();
        applySession(null);
        return false;
      }
      applySession(nextSession);
      return Boolean(nextSession);
    },
    [applySession],
  );

  const handleAuthData = useCallback(
    async (params: ReturnType<typeof parseAuthParamsFromUrl>) => {
      if (!supabase) return false;
      setIsLoading(true);
      try {
        const nextSession = await restoreSessionFromAuthParams(
          supabase,
          params,
        );
        return await applyApprovedSession(nextSession);
      } catch (error) {
        if (import.meta.env.DEV)
          console.error("[Auth] callback failed:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [applyApprovedSession],
  );

  const startPolling = useDesktopAuthCallbacks({
    enabled: Boolean(supabase),
    handleAuthData,
  });

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }
    const client: NonNullable<typeof supabase> = supabase;

    let isActive = true;
    async function restore() {
      try {
        const restored = await restoreWebAuthSession(client);
        const nextSession =
          restored.session ?? (await client.auth.getSession()).data.session;
        if (!isActive) return;
        if (restored.hasCallbackPayload) {
          clearWebAuthCallbackHistory(restored.returnPath);
        }
        await applyApprovedSession(nextSession);
      } catch (error) {
        if (import.meta.env.DEV)
          console.error("[Auth] session restore failed:", error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    }

    void restore();
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, nextSession) => {
      if (!isActive) return;
      void applyApprovedSession(event === "SIGNED_OUT" ? null : nextSession);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, [applyApprovedSession]);

  const signIn = useCallback(async () => {
    try {
      await disableTradingBlockerIfAvailable();
      if (window.electron) {
        const launchUrl = buildDesktopAuthBridgeUrl();
        if (window.electron.openExternal)
          window.electron.openExternal(launchUrl);
        else window.location.href = launchUrl;
        startPolling();
        return;
      }

      const data = await signInWithGoogle();
      if (data.url) window.location.href = data.url;
      else console.error("[Auth] Google sign-in returned no URL:", data);
    } catch (error) {
      console.error("[Auth] Google sign-in failed:", error);
    }
  }, [startPolling]);

  const signOut = useCallback(async () => {
    await signOutFromSupabase();
    applySession(null);
  }, [applySession]);

  return {
    isLoading,
    session,
    signIn,
    signOut,
    user,
  };
}

async function isApprovedSession(session: Session) {
  if (!isWaitlistOnly(import.meta.env.VITE_WAITLIST_ONLY)) return true;

  const response = await fetch(`${getRuntimeApiBase()}/api/marketing/access`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  }).catch(() => null);
  return response?.ok === true;
}

async function disableTradingBlockerIfAvailable() {
  if (!window.electron?.blocker) return;
  try {
    await window.electron.blocker.disableFast?.();
  } catch {
    /* ignore */
  }
  try {
    await window.electron.blocker.disable();
  } catch {
    /* ignore */
  }
}
